import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import * as moment from 'moment';
import { BehaviorSubject, EMPTY, Observable, Subject, timer } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ApplicationService implements OnDestroy {
    protected notificationSubject: BehaviorSubject<string[]>;
    protected notifications: string[] = [];
    protected version: BehaviorSubject<{ version: string; date: string }>;
    protected nVersion: BehaviorSubject<{ version: string; date: string }>;
    protected destroy: Subject<boolean> = new Subject();
    private _headerVisibilitySubj: Subject<boolean> = new Subject();
    private _userActionHistory: BehaviorSubject<{ context: string; action: any }[]> = new BehaviorSubject<
        { context: string; action: any }[]
    >([]);

    constructor(protected http: HttpClient, updates: SwUpdate, private router: Router) {
        this.notificationSubject = new BehaviorSubject<string[]>([]);
        this.nVersion = new BehaviorSubject(null);
        try {
            this.version = new BehaviorSubject(JSON.parse(localStorage.getItem('nk3-version')));
        } catch (e) {
            this.version = new BehaviorSubject(null);
        }

        // Auto-reload on navigation if new version is available
        this.setupNavigationVersionCheck();
        this.stripCacheBustingQueryParam();
        this.stripCacheBustingQueryParamOnNavigation();

        // updates.versionUpdates.subscribe((evt) => {
        //     switch (evt.type) {
        //         case 'VERSION_DETECTED':
        //             console.log(`Downloading new app version: ${evt.version.hash}`);
        //             break;
        //         case 'VERSION_READY':
        //             console.log(`Current app version: ${evt.currentVersion.hash}`);
        //             console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
        //             break;
        //         case 'VERSION_INSTALLATION_FAILED':
        //             console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
        //             break;
        //     }
        // });

        // updates.versionUpdates
        //     .pipe(
        //         filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        //         switchMap(() =>
        //             this.http.get<{ version: string; date: string }>(`version.json?_=${Date.now()}`)
        //         ),
        //         catchError(() => EMPTY),
        //         takeUntil(this.destroy)
        //     )
        //     .subscribe((data) => this.handleVersionUpdate(data));

        this.startVersionCheck();

        // SW-based detection (when SW is active)
        if (updates.isEnabled) {
            updates.versionUpdates
                .pipe(
                    filter((evt) => evt.type == 'VERSION_READY'),
                    switchMap(() =>
                        this.http.get<{ version: string; date: string }>(
                            `version.json?_=${Date.now()}`
                        )
                    ),
                    catchError(() => EMPTY),
                    takeUntil(this.destroy)
                )
                .subscribe((data) => {
                    this.nVersion.next(data);
                    this.notify('version-alert');
                });
        }
    }

    /**
     *
     * @returns {Observable<string[]>}
     */
    getNotifications(): Observable<string[]> {
        return this.notificationSubject.asObservable();
    }

    public notify(notificationName: string, data?: any) {
        this.notifications.push(notificationName);
        this.notificationSubject.next(this.notifications);
    }

    public removeNotification(notificationName: string) {
        this.notifications = this.notifications.filter((item) => item !== notificationName);
        this.notificationSubject.next(this.notifications);
    }

    public async reload(): Promise<void> {
        try {
            await this.clearAllCache();
            localStorage.removeItem('nk3-version');
            window.location.href = this.getCacheBustingReloadUrl();
        } catch (error) {
            console.error('Error during reload:', error);
            document.location.reload();
        }
    }

    /**
     * Keep the current path/query/hash and add a unique `t` so caches cannot
     * reuse a stale index.html after a version update.
     */
    public getCacheBustingReloadUrl(): string {
        const url = new URL(window.location.href);
        url.searchParams.set('t', String(Date.now()));
        return url.toString();
    }

    /**
     * `t` is only needed for the document request. After Angular has committed
     * the route, drop it from the address bar without triggering another navigation
     * (a Router navigate here would re-enter the version-check reload).
     */
    private stripCacheBustingQueryParamOnNavigation(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.destroy)
            )
            .subscribe(() => this.stripCacheBustingQueryParam());
    }

    private stripCacheBustingQueryParam(): void {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('t')) {
            return;
        }
        url.searchParams.delete('t');
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }

    /**
     * Clear all application cache including service worker caches
     */
    private async clearAllCache(): Promise<void> {
        // Clear service worker caches
        if ('caches' in window) {
            try {
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map((name) => caches.delete(name)));
                console.log('Service worker caches cleared');
            } catch (error) {
                console.error('Error clearing service worker cache:', error);
            }
        }

        // Clear session storage
        try {
            sessionStorage.clear();
        } catch (error) {
            console.error('Error clearing session storage:', error);
        }

        // Clear localStorage except for critical items
        try {
            const criticalKeys = ['user-preferences', 'token']; // Add your critical keys here
            const allKeys = Object.keys(localStorage);
            allKeys.forEach((key) => {
                if (!criticalKeys.includes(key)) {
                    localStorage.removeItem(key);
                }
            });
        } catch (error) {
            console.error('Error clearing localStorage:', error);
        }
    }

    public getVersion(): Observable<{ version: string; date: string }> {
        return this.version.asObservable();
    }

    public getNewVersion(): Observable<{ version: string; date: string }> {
        return this.nVersion.asObservable();
    }

    public hasPendingVersionUpdate(): boolean {
        return !!(this.nVersion.value && this.version.value);
    }

    public getVersionValue(): string {
        return this.version?.value?.version || this.nVersion?.value?.version || 'None';
    }

    protected notificationIsActive(notificationName): boolean {
        return this.notifications.indexOf(notificationName) > -1;
    }

    private startVersionCheck(): void {
        timer(0, 100_000)
            .pipe(
                switchMap(() =>
                    this.http.get<{ version: string; date: string }>(
                        `version.json?_=${Date.now()}`
                    )
                ),
                catchError(() => EMPTY),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                if (data && !this.version.value?.version) {
                    localStorage.setItem('nk3-version', JSON.stringify(data));
                    this.version.next(data);
                }
                if (
                    this.version.value?.version &&
                    this.version.value.version !== data?.version &&
                    !this.notificationIsActive('version-alert')
                ) {
                    this.nVersion.next(data);
                    this.notify('version-alert');
                }
            });
    }

    public setHeaderVisisbility(visible: boolean) {
        this._headerVisibilitySubj.next(visible);
    }

    public getHeaderVisibility(): Observable<boolean> {
        return this._headerVisibilitySubj.asObservable();
    }
    public setUserAction(userAction: { context: string; action: any }) {
        const currentActions = this._userActionHistory.value;
        const index = currentActions.findIndex((action) => action.context === userAction.context);
        if (index !== -1) {
            currentActions[index] = userAction;
        } else {
            currentActions.push(userAction);
        }
        this._userActionHistory.next(currentActions);
    }
    public getUserActionHistory(): Observable<{ context: string; action: any }[]> {
        return this._userActionHistory.asObservable();
    }
    public getUserActionByContext(context: string): Observable<any> {
        return this._userActionHistory
            .asObservable()
            .pipe(map((actions) => actions.find((action) => action.context == context)));
    }

    /**
     * Setup automatic reload on navigation when new version is detected
     * This prevents users from navigating to routes with missing chunks
     */
    private setupNavigationVersionCheck(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationStart),
                takeUntil(this.destroy)
            )
            .subscribe(async () => {
                if (this.hasPendingVersionUpdate()) {
                    await this.reload();
                }
            });
    }

    public ngOnDestroy(): void {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
